import { type KeyboardEventHandler, type ReactNode, type Ref } from 'react';
import {
  NumberInput as MantineNumberInput,
  NumberInputProps as MantineNumberInputProps,
} from '@mantine/core';
import clsx from 'clsx';

import { useFormattingLocale } from '../../utils/getFormattingLocales';
import {
  getDecimalSeparatorForLocale,
  getThousandsSeparatorForLocale,
} from '../../utils/numberLocale';
import { MAX_DECIMAL_SCALE } from '../../utils/numberPrecision';
import { ClearButton } from '../clearbutton/ClearButton';
import { FormLabel } from '../formlabel/FormLabel';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import inputStyles from '../input/input.module.scss';

const MAXIMUM = 999_999_999_999.999;
const MINIMUM = -MAXIMUM;

export type NumberInputProps = {
  allowDecimal?: boolean;
  clampBehaviour?: MantineNumberInputProps['clampBehavior'];
  clearable?: boolean;
  decimalScale?: number;
  description?: ReactNode;
  disabled?: boolean;
  disableThousandsSeparator?: boolean;
  error?: string;
  id?: string;
  inlineError?: boolean;
  label?: ReactNode | ReactNode[];
  loading?: boolean;
  max?: number;
  min?: number;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  suffix?: string;
  tableLayout?: boolean;
  testId?: string;
  value?: number | string;
  onBlur?: () => void;
  onChange?: (value: string | number) => void;
  onClear?: () => void;
  onKeyUp?: KeyboardEventHandler;
  ref?: Ref<HTMLInputElement>;
};

export function NumberInput({
  allowDecimal,
  clampBehaviour = 'strict',
  clearable,
  decimalScale = MAX_DECIMAL_SCALE,
  description,
  disabled,
  disableThousandsSeparator = false,
  error,
  id,
  inlineError,
  label,
  loading,
  max = MAXIMUM,
  min = MINIMUM,
  placeholder,
  readOnly = false,
  ref,
  required,
  suffix,
  tableLayout = false,
  testId,
  value,
  onBlur,
  onChange,
  onClear,
  onKeyUp,
}: NumberInputProps) {
  const locale = useFormattingLocale();
  const showClearAction = value != null && !loading && !disabled;

  let rightSection: ReactNode | undefined;
  if (loading) {
    rightSection = <LoadingIndicator data-testid="input-loading-indicator" />;
  } else if (clearable) {
    rightSection = (
      <ClearButton
        hidden={!showClearAction}
        disabled={disabled || readOnly}
        onClear={onClear}
      />
    );
  }

  return (
    <div className={clsx({ [inputStyles['root--table-layout']]: tableLayout })}>
      <FormLabel
        description={description}
        id={id}
        required={required}
        disabled={disabled}
        loading={loading}
        error={error || undefined}
        inline={inlineError}
        label={label}
      />
      <MantineNumberInput
        unstyled
        allowDecimal={allowDecimal}
        data-testid={testId}
        decimalScale={Math.min(decimalScale, MAX_DECIMAL_SCALE)}
        clampBehavior={clampBehaviour}
        classNames={{
          wrapper: inputStyles['wrapper'],
          input: clsx(inputStyles['input'], {
            [inputStyles['input--error']]: error != null,
          }),
          section: inputStyles['section'],
        }}
        max={Math.min(max, MAXIMUM)}
        min={Math.max(min, MINIMUM)}
        disabled={disabled || loading}
        id={id}
        placeholder={placeholder}
        hideControls={true}
        decimalSeparator={getDecimalSeparatorForLocale(locale)}
        readOnly={readOnly}
        rightSection={rightSection}
        rightSectionPointerEvents={showClearAction ? 'auto' : 'none'}
        onBlur={onBlur}
        onChange={(value) => onChange?.(value)}
        onKeyUp={onKeyUp}
        ref={ref}
        suffix={suffix}
        thousandSeparator={getThousandsSeparatorForLocale(
          disableThousandsSeparator,
          locale
        )}
        value={value ?? ''}
      />
    </div>
  );
}
